import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { StatusScreen } from '@/components/StatusScreen';
import { INTEGRATIONS_FUNCTION } from '@/lib/chatFunction';

export default function SupabaseCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;

    // If not authenticated, redirect to login preserving the current URL
    if (!user) {
      const returnUrl = location.pathname + location.search;
      navigate('/auth', {
        replace: true,
        state: { returnUrl }
      });
      return;
    }

    const exchangeCode = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const codeVerifier = sessionStorage.getItem('supabase_code_verifier');
      const savedState = sessionStorage.getItem('supabase_oauth_state');

      // Validate state to prevent CSRF
      if (!state || state !== savedState) {
        setStatus('error');
        setError('État invalide. Veuillez réessayer.');
        return;
      }

      if (!code) {
        setStatus('error');
        setError('Code d\'autorisation manquant');
        return;
      }

      if (!codeVerifier) {
        setStatus('error');
        setError('Code verifier manquant. Veuillez réessayer.');
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke(INTEGRATIONS_FUNCTION, {
          body: {
            action: 'exchangeSupabaseOAuthCode',
            code,
            codeVerifier,
          },
        });

        if (error) throw new Error(error.message);
        if (data?.error) throw new Error(data.error);

        // Clean up session storage
        sessionStorage.removeItem('supabase_code_verifier');
        sessionStorage.removeItem('supabase_oauth_state');

        setStatus('success');

        // Redirect after success
        setTimeout(() => {
          navigate('/?supabase=connected', { replace: true });
        }, 1500);
      } catch (err) {
        console.error('Supabase OAuth error:', err);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Erreur lors de la connexion');
      }
    };

    exchangeCode();
  }, [searchParams, navigate, user, isLoading, location]);

  if (status === 'loading') {
    return <StatusScreen status="loading" title="Connexion à Supabase…" />;
  }

  if (status === 'success') {
    return <StatusScreen status="success" title="Compte Supabase connecté" meta="Redirection…" />;
  }

  return (
    <StatusScreen
      status="error"
      title="Erreur de connexion"
      meta={error ?? undefined}
      action={{ label: "Retour à l'accueil", onClick: () => navigate('/', { replace: true }) }}
    />
  );
}
